import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography"; // ✅ Added Import
import CircularProgress from "@mui/material/CircularProgress"; // ✅ Added Import
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import ChartHeader from "examples/Charts/BarCharts/HorizontalBarChart/ChartHeader"; // ✅ Use Horizontal specific
import MetricCardsContainer from "examples/Charts/BarCharts/HorizontalBarChart/MetricCardsContainer"; // ✅ Use Horizontal specific
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled"; // ✅ Added Import
import { generateColors } from "../../../utils/chartColors";

// -----------------------------------------------------------
// 🚀 HELPER: ISO Country Code to Name
// -----------------------------------------------------------
const getCountryName = (code) => {
    try {
        const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
        return regionNames.of(code) || code;
    } catch (e) {
        return code; // Fallback
    }
};

// -----------------------------------------------------------
// 🚀 HELPER: Parse Audience Country
// -----------------------------------------------------------
const parseCountryDemographics = (profile) => {
    let rawCountries = null;

    // 1. Facebook Path
    if (profile.pageDemographics && profile.pageDemographics.page_follows_country) {
        rawCountries = profile.pageDemographics.page_follows_country;
    }
    // 2. Instagram Path (profileDemographics on root)
    else if (profile.profileDemographics) {
        rawCountries = profile.profileDemographics.audience_country;
    }
    // 3. YouTube Path (channelDemographics on root)
    else if (profile.channelDemographics?.country && Object.keys(profile.channelDemographics.country).length > 0) {
        rawCountries = profile.channelDemographics.country;
    }
    // 4. Fallback: Search in 'data' array (for historical or derived structures)
    else if (profile.data && profile.data.length > 0) {
        const sortedData = [...profile.data].sort((a, b) => new Date(b.date || b.datetime || 0) - new Date(a.date || a.datetime || 0));

        const latestItem = sortedData.find(item =>
            item.profileDemographics?.audience_country ||
            item.pageDemographics?.page_follows_country ||
            (item.channelDemographics?.country && Object.keys(item.channelDemographics.country).length > 0) // ✅ Robust Check
        );

        if (latestItem) {
            if (latestItem.pageDemographics?.page_follows_country) {
                rawCountries = latestItem.pageDemographics.page_follows_country;
            } else if (latestItem.profileDemographics?.audience_country) {
                rawCountries = latestItem.profileDemographics.audience_country;
            } else if (latestItem.channelDemographics?.country) {
                rawCountries = latestItem.channelDemographics.country;
            }
        }
    }

    if (!rawCountries || Object.keys(rawCountries).length === 0) return null;

    // Sort and Take Top 10
    const sortedEntries = Object.entries(rawCountries)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);

    const labels = sortedEntries.map(([code]) => getCountryName(code));
    const data = sortedEntries.map(([, val]) => val);
    const rawData = sortedEntries.map(([code, val]) => ({ [code]: val })); // Keep raw code for reference if needed

    // Generate Colors
    // Generate Colors
    const backgroundColors = generateColors(labels.length, []);

    return {
        labels,
        datasets: {
            label: "Audience Country",
            data: data,
            backgroundColors: backgroundColors // HorizontalBarChart might ignore this if it uses theme, but good to pass
        },
        topCountry: labels[0] || "N/A",
        topValue: data[0] || 0
    };
};

function AudienceCountryChart({
    title,
    datasets,
    previousdatasets,
    isLoading,
    entitlements,
    is_derived, // ✅ Preserve existing prop
    viewMode, // ✅ Preserve existing prop
    onViewModeChange, // ✅ Preserve existing prop
    isEntitled = true, // ✅ Added
    featureName = "Demographics Report", // ✅ Added
    planRequired = "pro" // ✅ Added
}) {

    // 1. Calculate Chart Data
    const chartDataByProfile = useMemo(() => {
        if (!datasets || datasets.length === 0) return [];

        return datasets.map(profile => {
            const parsed = parseCountryDemographics(profile);
            if (!parsed) return null;

            return {
                profileName: profile.label || profile.name || "Profile",
                profileId: profile.profileId,
                platform: profile.platform,
                profilePictureUrl: profile.profilePictureUrl,
                chart: {
                    labels: parsed.labels,
                    datasets: parsed.datasets
                },
                topCountry: parsed.topCountry,
                topValue: parsed.topValue
            };
        }).filter(Boolean);
    }, [datasets]);

    // 2. Format Data for Reusable Component
    // HorizontalBarChart usually takes a simplified structure compared to complex multi-dataset charts,
    // or handles standard Chart.js structures.
    // Based on HorizontalBarChart/index.js, it expects `chart.labels` and `chart.datasets`.

    // We aggregate data for the reusable component.
    // Since HorizontalBarChart renders ONE chart, we might need to merge datasets if multiple profiles are selected?
    // OR does HorizontalBarChart handle multiple datasets (bars per label)?
    // Usually standard bar charts do.

    // However, if countries are different per profile, labels might mismatch.
    // Strategy: Union of labels (all top countries from all profiles) and map data.

    const unifiedData = useMemo(() => {
        if (chartDataByProfile.length === 0) return null;

        // Union of all labels
        const allLabels = new Set();
        chartDataByProfile.forEach(p => p.chart.labels.forEach(l => allLabels.add(l)));
        const labels = Array.from(allLabels).sort(); // Alphabetical or by total?

        // Generate Colors outside loop
        const profileIds = chartDataByProfile.map(p => p.profileId);
        const colors = generateColors(chartDataByProfile.length, profileIds);

        const finalDatasets = chartDataByProfile.map((p, index) => {
            // Standardize colors

            return {
                label: p.profileName,
                data: labels.map(label => {
                    const idx = p.chart.labels.indexOf(label);
                    return idx >= 0 ? p.chart.datasets.data[idx] : 0;
                }),
                backgroundColor: colors[index],
                profilePictureUrl: p.profilePictureUrl,
                platform: p.platform,
                metricName: "Top Country", // For Metric Card
            };
        });

        return {
            labels: labels,
            datasets: finalDatasets
        };

    }, [chartDataByProfile]);

    // 3. Metrics for Cards
    const metrics = useMemo(() => {
        // Parse Previous Data
        const previousDataMap = {};
        if (previousdatasets && previousdatasets.length > 0) {
            previousdatasets.forEach(p => {
                const parsed = parseCountryDemographics(p);
                if (parsed) {
                    previousDataMap[p.profileId] = parsed.topCountry;
                }
            });
        }

        // 🟢 Derive baseline metrics from datasets if chartDataByProfile is empty
        const profilesToMap = (chartDataByProfile && chartDataByProfile.length > 0)
            ? chartDataByProfile
            : (datasets || []);

        return profilesToMap.map(profile => {
            const isParsed = profile.hasOwnProperty('topCountry');
            return {
                profilePic: profile.profilePictureUrl || profile.profilePic || "",
                name: profile.profileName || profile.label || profile.name || "Profile",
                platform: profile.platform,
                metricName: "Top Country",
                currentMetricValue: isParsed ? profile.topCountry : "N/A",
                previousMetricValue: previousDataMap[profile.profileId || profile.id] || "N/A",
                isQualitative: true
            };
        });
    }, [chartDataByProfile, datasets, previousdatasets]);


    if (isLoading) {
        return (
            <Card sx={{ height: "100%" }}>
                <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="300px">
                    <CircularProgress size={32} thickness={4} color="accent" />
                </MDBox>
            </Card>
        );
    }

    // 🟢 DYNAMIC HEIGHT CALCULATION
    // Base 100px + 50px per country (label)
    const dynamicHeight = unifiedData ? Math.max(400, unifiedData.labels.length * 50) : 400;

    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                This chart shows the geographic distribution of your audience by country. It helps you understand where your followers are located globally.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                - <strong>Longer bars</strong>: Indicate countries with a larger share of your audience. <br />
                - <strong>Shorter bars</strong>: Represent countries with fewer followers. <br />
                - <strong>Top Country</strong>: The country at the top (or sorted highest) is your primary market. <br />
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Align content timing with the time zones of your top countries. <br />
                - Localize campaigns or language for key regions. <br />
                - Identify potential new markets for expansion. <br />
            </Typography>
        </div>
    );

    return (
        <HorizontalBarChart
            title={title}
            description={tooltipContent}
            height={`${dynamicHeight}px`} // 🟢 Pass Dynamic Height
            chart={unifiedData || { labels: [], datasets: [] }} // ✅ Ensure not null
            metrics={metrics}
            is_derived={is_derived}
            entitlements={entitlements}
            isEntitled={isEntitled} // ✅ Pass entitlement
            featureName={featureName}
            planRequired={planRequired}
        />
    );
}

AudienceCountryChart.propTypes = {
    title: PropTypes.string,
    datasets: PropTypes.array,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    is_derived: PropTypes.bool,
    entitlements: PropTypes.object,
    viewMode: PropTypes.string,
    onViewModeChange: PropTypes.func,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
};

export default AudienceCountryChart;
